import React from 'react';
import { Input, Button, Radio, Checkbox, DatePicker } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import type { RadioChangeEvent } from 'antd';
import type { Dayjs } from 'dayjs';

interface QueryParamsProps {
  payeeFrom: string;
  payeeTo: string;
  salesFrom: string;
  salesTo: string;
  dateFrom: string;
  dateTo: string;
  shipNoFrom: string;
  shipNoTo: string;
  includeZero: boolean;
  reportType: string;
  loading: boolean;
  onPayeeFromChange: (v: string) => void;
  onPayeeToChange: (v: string) => void;
  onSalesFromChange: (v: string) => void;
  onSalesToChange: (v: string) => void;
  onDateFromChange: (v: string) => void;
  onDateToChange: (v: string) => void;
  onShipNoFromChange: (v: string) => void;
  onShipNoToChange: (v: string) => void;
  onIncludeZeroChange: (v: boolean) => void;
  onReportTypeChange: (v: string) => void;
  onQuery: () => void;
}

const QueryParams: React.FC<QueryParamsProps> = ({
  payeeFrom, payeeTo, salesFrom, salesTo, dateFrom, dateTo,
  shipNoFrom, shipNoTo, includeZero, reportType, loading,
  onPayeeFromChange, onPayeeToChange, onSalesFromChange, onSalesToChange,
  onDateFromChange, onDateToChange, onShipNoFromChange, onShipNoToChange,
  onIncludeZeroChange, onReportTypeChange, onQuery,
}) => {
  const handleDateFromChange = (_: Dayjs | null, dateStr: string | string[]) => {
    onDateFromChange(Array.isArray(dateStr) ? dateStr[0] || '' : dateStr || '');
  };
  const handleDateToChange = (_: Dayjs | null, dateStr: string | string[]) => {
    onDateToChange(Array.isArray(dateStr) ? dateStr[0] || '' : dateStr || '');
  };

  return (
    <div className="bg-gray-50 border border-gray-200 rounded p-4 mb-4">
      {/* Row 1: Payee range */}
      <div className="flex flex-wrap items-center gap-4 mb-3">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium" style={{ width: 110 }}>佣金受款人[從]</span>
          <Input
            value={payeeFrom}
            onChange={(e) => onPayeeFromChange(e.target.value)}
            placeholder="起"
            style={{ width: 130 }}
            maxLength={12}
          />
          <span className="text-sm">[至]</span>
          <Input
            value={payeeTo}
            onChange={(e) => onPayeeToChange(e.target.value)}
            placeholder="迄"
            style={{ width: 130 }}
            maxLength={12}
          />
        </div>
      </div>

      {/* Row 2: Sales range */}
      <div className="flex flex-wrap items-center gap-4 mb-3">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium" style={{ width: 110 }}>業務代號[從]</span>
          <Input
            value={salesFrom}
            onChange={(e) => onSalesFromChange(e.target.value)}
            placeholder="起"
            style={{ width: 130 }}
            maxLength={8}
          />
          <span className="text-sm">[至]</span>
          <Input
            value={salesTo}
            onChange={(e) => onSalesToChange(e.target.value)}
            placeholder="迄"
            style={{ width: 130 }}
            maxLength={8}
          />
        </div>
      </div>

      {/* Row 3: Date range */}
      <div className="flex flex-wrap items-center gap-4 mb-3">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium" style={{ width: 110 }}>交運日期[從]</span>
          <DatePicker
            value={dateFrom ? undefined : undefined}
            onChange={handleDateFromChange}
            placeholder="起始日期"
            style={{ width: 130 }}
            format="YYYY-MM-DD"
          />
          <span className="text-sm">[至]</span>
          <DatePicker
            value={dateTo ? undefined : undefined}
            onChange={handleDateToChange}
            placeholder="結束日期"
            style={{ width: 130 }}
            format="YYYY-MM-DD"
          />
        </div>
      </div>

      {/* Row 4: Ship number range */}
      <div className="flex flex-wrap items-center gap-4 mb-3">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium" style={{ width: 110 }}>交運單號[從]</span>
          <Input
            value={shipNoFrom}
            onChange={(e) => onShipNoFromChange(e.target.value)}
            placeholder="起"
            style={{ width: 130 }}
            maxLength={7}
          />
          <span className="text-sm">[至]</span>
          <Input
            value={shipNoTo}
            onChange={(e) => onShipNoToChange(e.target.value)}
            placeholder="迄"
            style={{ width: 130 }}
            maxLength={7}
          />
        </div>
      </div>

      {/* Row 5: Zero balance option */}
      <div className="flex flex-wrap items-center gap-4 mb-3">
        <span className="text-sm font-medium" style={{ width: 110 }}>餘額為零</span>
        <Radio.Group
          value={includeZero ? 1 : 2}
          onChange={(e: RadioChangeEvent) => onIncludeZeroChange(e.target.value === 1)}
        >
          <Radio value={1}>包含</Radio>
          <Radio value={2}>排除</Radio>
        </Radio.Group>
      </div>

      {/* Row 6: Report type */}
      <div className="flex flex-wrap items-center gap-4 mb-3">
        <span className="text-sm font-medium" style={{ width: 110 }}>印表選擇</span>
        <Checkbox
          checked={reportType === 'payee' || reportType === 'both'}
          onChange={(e) => {
            const payee = e.target.checked;
            const sales = reportType === 'sales' || reportType === 'both';
            if (payee && sales) onReportTypeChange('both');
            else if (payee) onReportTypeChange('payee');
            else if (sales) onReportTypeChange('sales');
            else onReportTypeChange('');
          }}
        >
          受款人別
        </Checkbox>
        <Checkbox
          checked={reportType === 'sales' || reportType === 'both'}
          onChange={(e) => {
            const sales = e.target.checked;
            const payee = reportType === 'payee' || reportType === 'both';
            if (payee && sales) onReportTypeChange('both');
            else if (sales) onReportTypeChange('sales');
            else if (payee) onReportTypeChange('payee');
            else onReportTypeChange('');
          }}
        >
          業務別
        </Checkbox>
      </div>

      {/* Query button */}
      <div className="flex gap-2">
        <Button type="primary" icon={<SearchOutlined />} onClick={onQuery} loading={loading}>
          查詢
        </Button>
      </div>
    </div>
  );
};

export default QueryParams;
